import React, { useCallback } from 'react';
import { IssueCommonErrorBoundary } from '@atlassian/jira-issue-commmon-error-boundary/src/ui/index.tsx';
import type { AnalyticsAttributes } from '@atlassian/jira-product-analytics-bridge/src/types.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { ExtraErrorAttributes } from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import {
	fireOperationalAnalyticsDeferred,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { JiraIssueCreateErrorBoundaryProps } from './types.tsx';

export const JiraIssueCreateErrorBoundary = (props: JiraIssueCreateErrorBoundaryProps) => {
	const { children, attributes, onError, taskName, ...otherProps } = props;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const getAttributes = useCallback(
		(error: Error): AnalyticsAttributes => {
			// TODO: add additional attributes in that we need for issue create.
			const attributesPassed = typeof attributes === 'function' ? attributes(error) : attributes;
			return {
				...attributesPassed,
			};
		},
		[attributes],
	);

	const handleOnError = useCallback(
		(location: string, error: Error, errorAttributes: ExtraErrorAttributes) => {
			if (taskName) {
				const analyticsEvent = createAnalyticsEvent({});
				fireOperationalAnalyticsDeferred(analyticsEvent, 'ui taskFail', {
					task: taskName,
				});
			}

			onError?.(location, error, errorAttributes);
		},
		[createAnalyticsEvent, onError, taskName],
	);

	if (fg('jira-issue-create-error-boundary-enabled')) {
		return (
			<IssueCommonErrorBoundary
				{...otherProps}
				attributes={getAttributes}
				onError={fg('global_issue_create_modal_view_task_fail_event') ? handleOnError : onError}
			>
				{children}
			</IssueCommonErrorBoundary>
		);
	}
	return (
		<JSErrorBoundary
			{...otherProps}
			onError={fg('global_issue_create_modal_view_task_fail_event') ? handleOnError : onError}
		>
			{children}
		</JSErrorBoundary>
	);
};
